import httpService from "./httpService";
import type { FactorsResponse } from "./productFactorsService";
import type { ProductRiskMetrics } from "../types/productRiskMetrics";
import type { ProductPerformance } from "../types/productV3";

const PUBLIC_FACTORS_PAGE_SIZE = 10000;

// UTM Parameters for tracking
export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

// Metadata for generating UTM parameters
export interface ShareableResourceMetadata {
  resourceName: string;
  resourceType: "BASKET" | "PORTFOLIO";
  resourceId: number;
  providerName: string;
  providerId: number;
}

// Shareable Links API service
export interface ShareableLink {
  id: number;
  url: string;
  type: "BASKET" | "PORTFOLIO";
  resourceId: number;
  providerId: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string | null;
  isActive: boolean;
  accessCount: number;
}

export interface CreateShareableLinkRequest {
  type: "BASKET" | "PORTFOLIO";
  resourceId: number;
}

export interface CreateShareableLinkResponse {
  message: string;
  shareableLink: ShareableLink;
}

export interface ShareableLinksResponse {
  message: string;
  shareableLinks: ShareableLink[];
}

export interface AccessShareableLinkResponse {
  message: string;
  type: "BASKET" | "PORTFOLIO";
  resourceId: number;
  providerId: number;
  resourceData: any;
}

export interface ProductShareMetadata {
  productType: "BASKET" | "PORTFOLIO";
  productId: number;
  providerId: number;
  providerName: string;
  createdAtUtc: string;
  visibility: "PUBLIC" | "PRIVATE" | "SUBSCRIBER";
}

export interface ProductShareAggregation {
  title: string;
  description: string;
  createdAt: string;
  totalStocks: number | null;
  totalLongStocks: number | null;
  totalShortStocks: number | null;
  totalBaskets: number | null;
  totalLongBaskets: number | null;
  totalShortBaskets: number | null;
  totalOptions: number | null;
  totalLongOptions: number | null;
  totalShortOptions: number | null;
  totalHoldings: number | null;
  totalLongHoldings: number | null;
  totalShortHoldings: number | null;
}

export type ProductShareChangeSpan = "7d" | "30d" | "90d";

export interface ProductShareChangeCount {
  span: ProductShareChangeSpan;
  value: number;
}

export interface ProductShareProviderSummary {
  numOfPortfolios: number;
  numOfBaskets: number;
}

export interface LegacyProductShareRiskMetrics {
  computedAtUtc: string;
  params: {
    riskFreeRate: number;
  };
  windowStartUtc: string;
  windowEndUtc: string;
  metrics?: Array<{ name: string; value: number }>;
  benchmarks?: Array<{
    conId: number;
    symbol: string;
    metrics?: Array<{ name: string; value: number }>;
  }>;
}

export interface PublicTopPortfolioStat {
  productId: number;
  providerId: number | null;
  title: string;
  numOfStocks: number;
  numOfOptions: number;
  performance: ProductPerformance;
}

export interface PublicProductStats {
  numOfPortfolios: number;
  numOfBaskets: number;
  topPortfolios: PublicTopPortfolioStat[];
}

export interface PublicProviderLite {
  id: number;
  name: string;
  portfolios: {
    public: number;
    subscribed: number;
  };
  baskets: {
    public: number;
    subscribed: number;
  };
}

export interface ShareContractEntry {
  conId: number;
  weight: number;
}

export interface ShareBasketCompositionEntry {
  basketId: number;
  weight: number;
}

export interface ShareComposition {
  contracts: {
    stocks: ShareContractEntry[];
    options?: ShareContractEntry[] | null;
  };
  baskets?: ShareBasketCompositionEntry[] | null;
}

export interface ShareBasketDetail {
  basketId: number;
  visibility: "PUBLIC" | "PRIVATE" | "SUBSCRIBER";
  composition: {
    contracts: {
      stocks: ShareContractEntry[];
    };
  } | null;
}

export interface ProductShareDataResponse {
  data: {
    metadata: ProductShareMetadata;
    aggregation: ProductShareAggregation;
    composition: ShareComposition | null;
    baskets?: ShareBasketDetail[] | null;
    lastModifiedAtUtc: string | null;
    numOfChanges: ProductShareChangeCount[];
    riskMetrics: ProductRiskMetrics | LegacyProductShareRiskMetrics | null;
    numOfLikes: number;
    numOfComments: number;
    provider: ProductShareProviderSummary;
  };
}

interface PublicProductStatsResponse {
  data: PublicProductStats;
}

interface PublicProviderListResponse {
  message: string;
  providers: PublicProviderLite[];
}

export interface ContractLite {
  conId: number;
  symbol: string;
  longName: string;
  secType: string;
  currency: string;
  primaryExchange: string | null;
}

export interface OptionChainLite {
  conId: number;
  symbol: string;
  strikePrice: string;
  expiryDateLocal: string;
  putCall: "P" | "C";
  exchange: string;
}

class ShareableLinksService {
  constructor() {
    // No need to store baseUrl as httpService handles it
  }

  async createShareableLink(providerId: string, request: CreateShareableLinkRequest): Promise<ShareableLink> {
    try {
      const response = await httpService.post<CreateShareableLinkResponse>(
        `providers/${providerId}/shareable-links`,
        request
      );
      return response.data.shareableLink;
    } catch (error) {
      console.error("Create shareable link error:", error);
      throw error;
    }
  }

  async getProviderShareableLinks(providerId: string): Promise<ShareableLink[]> {
    try {
      const response = await httpService.get<ShareableLinksResponse>(`providers/${providerId}/shareable-links`);
      return response.data.shareableLinks;
    } catch (error) {
      console.error("Get provider shareable links error:", error);
      throw error;
    }
  }

  async getUserShareableLinks(): Promise<ShareableLink[]> {
    try {
      const response = await httpService.get<ShareableLinksResponse>("user/shareable-links");
      return response.data.shareableLinks;
    } catch (error) {
      console.error("Get user shareable links error:", error);
      throw error;
    }
  }

  async deactivateShareableLink(linkId: number): Promise<void> {
    try {
      await httpService.put(`shareable-links/${linkId}/deactivate`);
    } catch (error) {
      console.error("Deactivate shareable link error:", error);
      throw error;
    }
  }

  async accessShareableLink(url: string): Promise<AccessShareableLinkResponse> {
    try {
      const response = await httpService.get<AccessShareableLinkResponse>(`share/${url}`, { requiresAuth: false });
      return response.data;
    } catch (error) {
      console.error("Access shareable link error:", error);
      throw error;
    }
  }

  async getProductShareData(token: string): Promise<ProductShareDataResponse> {
    try {
      const response = await httpService.get<ProductShareDataResponse>(
        `v3/public/share/${token}`,
        { requiresAuth: false }
      );
      return response.data;
    } catch (error) {
      console.error("Get product share data error:", error);
      throw error;
    }
  }

  async getPublicProductStats(): Promise<PublicProductStats> {
    const response = await httpService.get<PublicProductStatsResponse>(
      "v3/public/product-stats",
      { requiresAuth: false }
    );
    return response.data.data;
  }

  async getPublicProviders(providerIds: number[]): Promise<PublicProviderLite[]> {
    const uniqueProviderIds = Array.from(new Set(providerIds));
    if (uniqueProviderIds.length === 0) return [];

    const response = await httpService.get<PublicProviderListResponse>(
      `v3/public/providers/${uniqueProviderIds.join(",")}`,
      { requiresAuth: false }
    );
    return response.data.providers;
  }

  async getPublicProviderInfo(providerId: number): Promise<{ id: number; name: string } | null> {
    try {
      const response = await httpService.get<{ providers: { id: number; name: string }[] }>(
        `v2/public/providers/${providerId}`,
        { requiresAuth: false }
      );
      const provider = response.data.providers?.[0];
      return provider ? { id: provider.id, name: provider.name } : null;
    } catch (error) {
      console.error("Get public provider info error:", error);
      return null;
    }
  }

  async regenerateSocialCardImage(providerId: string, token: string): Promise<void> {
    try {
      await httpService.post(`providers/${providerId}/shareable-links/${token}/regenerate-social-card-image`);
    } catch (error) {
      console.error("Regenerate social card image error:", error);
      throw error;
    }
  }

  async getPublicContracts(conIds: number[]): Promise<ContractLite[]> {
    const results: ContractLite[] = [];
    const batchSize = 30;
    for (let i = 0; i < conIds.length; i += batchSize) {
      const batch = conIds.slice(i, i + batchSize);
      const response = await httpService.get<{ contracts: ContractLite[] }>(
        `v3/public/contracts/${batch.join(",")}`,
        { requiresAuth: false }
      );
      results.push(...response.data.contracts);
    }
    return results;
  }

  async getPublicOptionChains(conIds: number[]): Promise<OptionChainLite[]> {
    if (conIds.length === 0) return [];
    const response = await httpService.get<{ optionContracts: OptionChainLite[] }>(
      `v3/public/option-chains/${conIds.join(",")}`,
      { requiresAuth: false }
    );
    return response.data.optionContracts;
  }

  async getPublicFactorsPage(
    providerId: number,
    productType: "basket" | "portfolio",
    productId: number,
    page: number,
    pageSize: number = PUBLIC_FACTORS_PAGE_SIZE,
    to?: string
  ): Promise<FactorsResponse> {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (to) params.set("to", to);

    const response = await httpService.get<FactorsResponse>(
      `v3/public/factors/providers/${providerId}/${productType}/${productId}?${params.toString()}`,
      { requiresAuth: false }
    );
    return response.data;
  }

  async getPublicContractFactors(conId: number): Promise<FactorsResponse["items"]> {
    const factors: FactorsResponse["items"] = [];
    let pageNumber = 1;
    let totalPages = 1;

    do {
      const params = new URLSearchParams();
      params.set("conId", String(conId));
      params.set("pageNumber", String(pageNumber));
      params.set("pageSize", String(PUBLIC_FACTORS_PAGE_SIZE));

      const response = await httpService.get<FactorsResponse>(
        `v3/public/contracts/factors?${params.toString()}`,
        { requiresAuth: false }
      );
      factors.push(...response.data.items);
      totalPages = response.data.totalPages;
      pageNumber += 1;
    } while (pageNumber <= totalPages);

    return factors.sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());
  }

  // Helper method to slugify text for UTM parameters
  private slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  // Generate UTM parameters based on resource metadata
  generateUTMParams(metadata: ShareableResourceMetadata, source: string = "plutus_share"): UTMParams {
    const campaign = this.slugify(metadata.resourceName);
    const content = this.slugify(metadata.providerName);
    const term = `${metadata.resourceType.toLowerCase()}-${metadata.resourceId}`;

    return {
      utm_source: source,
      utm_medium: source === "twitter" ? "social" : "shareable_link",
      utm_campaign: campaign,
      utm_content: content,
      utm_term: term,
    };
  }

  // Helper method to get the full shareable URL with UTM parameters
  // /picks/ is routed through the load balancer defined in the terraform config
  // This is routed directly to the API server which returns a server side generate
  // page iframing in the exisiting /share/ content
  getShareableUrl(token: string, metadata?: ShareableResourceMetadata, source?: string): string {
    const baseUrl = `${window.location.origin}/picks/${token}`;

    if (metadata) {
      const utmParams = this.generateUTMParams(metadata, source);
      const params = new URLSearchParams(utmParams as Record<string, string>);
      return `${baseUrl}?${params.toString()}`;
    }

    return baseUrl;
  }
}

export const shareableLinksService = new ShareableLinksService();
